import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AbstractRequestDto } from '@app/core/dto/abstract-request.dto';
import { SessionDto } from '@app/core/dto/session.dto';
import { ModuleType } from '@app/core/enums/module-type.enum';
import { SessionStorageService } from '@app/core/services/session/session-storage.service';
import { StringUtils } from '@app/core/utils/string.utils';
import { ASSISTED_USER_STORAGE_KEY } from '@repo/ui/lib/salesiq';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private _http: HttpClient = inject(HttpClient);
  private readonly _sessionStorage = inject(SessionStorageService);

  // Assisted-sales flag: mirrored into sessionStorage (so same-origin apps like
  // support-app can read it) and broadcast so consumers (e.g. AppComponent) can
  // decide whether to load the Zoho SalesIQ widget. `null` = session not resolved.
  private readonly _assistedUserSubject = new BehaviorSubject<boolean | null>(
    null,
  );
  readonly isAssistedUser$ = this._assistedUserSubject.asObservable();

  setAssistedUserFromSession(session: SessionDto | null | undefined): void {
    const isAssisted = !!session?.isAssistedUser;
    this._sessionStorage.set(ASSISTED_USER_STORAGE_KEY, isAssisted);
    this._assistedUserSubject.next(isAssisted);
  }

  getPagefetchData(
    module: ModuleType,
    body: AbstractRequestDto,
  ): Observable<any> {
    const url: string = this._getUrl(module, 'page/fetch');
    return this._http.post(url, body);
  }

  getActionData(module: ModuleType, body: AbstractRequestDto): Observable<any> {
    const url: string = this._getUrl(module, 'action');
    return this._http.post(url, body);
  }

  logout(module: ModuleType, body: AbstractRequestDto): Observable<any> {
    const url: string = this._getUrl(module, 'action?logout=true');
    return this._http.post(url, body);
  }

  getPartialActionData(
    module: ModuleType,
    body: AbstractRequestDto,
  ): Observable<any> {
    const url: string = this._getUrl(module, 'partial/action');
    return this._http.post(url, body);
  }

  callVerificationApi(body: any): Observable<any> {
    return this._http.post('/api/entity-verification', body);
  }

  callFileUploadApi(body: any): Observable<any> {
    return this._http.post('/api/storage/upload', body);
  }

  callFileDeleteApi(body: any): Observable<any> {
    return this._http.post('/api/storage/delete', body);
  }

  getUser(body: any): Observable<any> {
    const url: string = this._getUrl(ModuleType.USER, 'me');
    return this._http.post(url, body);
  }

  getCreditStatus(body: any): Observable<any> {
    const url: string = this._getUrl(ModuleType.USER, 'credit/status');
    return this._http.post(url, body);
  }

  getPromosData(body: any): Observable<any> {
    return this._http.post("/api/promo/page/fetch", body);
  }

  async getCloudflareHeaders() {
    const url: string = 'https://location.industrybuying.com';
    const response = await fetch(url, { method: 'GET' });
    const data = await response.json();
    return data;
  }

  refreshPage(pageUrl: string) {
    const body = { pageUrl };
    return this._http.post(
      'https://prerender.devops-806.workers.dev/api/cache/purge',
      body,
    );
  }

  getAutoSuggest(payload: any): Observable<any> {
    return this._http.post('/api/search/autosuggest/', payload);
  }

  private _getUrl(module: ModuleType, endpoint: string): string {
    let urlVariable: string;
    switch (module) {
      case ModuleType.CATALOG:
        urlVariable = 'catalog';
        break;

      case ModuleType.CHECKOUT:
        urlVariable = 'checkout';
        break;

      case ModuleType.USER:
        urlVariable = 'users';
        break;

      case ModuleType.ORDER:
        urlVariable = ModuleType.ORDER;
        break;
      case ModuleType.NPS:
        urlVariable = ModuleType.NPS;
        break;
      case ModuleType.ARTICLES:
        urlVariable = 'articles';
        break;

      default:
        urlVariable = 'catalog';
        break;
    }
    return StringUtils.format('/api/{0}/{1}', urlVariable, endpoint);
  }
}
